import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

// Auth Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Dashboard Pages
import Dashboard from './pages/Dashboard';
import Locations from './pages/Locations';
import Channels from './pages/Channels';
import Announcements from './pages/Announcements';
import Jingles from './pages/Jingles';
import RadioPrograms from './pages/RadioPrograms';
import Calendar from './pages/Calendar';
import Shoutouts from './pages/Shoutouts';
import QRCodeGenerator from './pages/QRCodeGenerator';
import News from './pages/News';
import Ads from './pages/Ads';
import BroadcastStatus from './pages/BroadcastStatus';
import PlatformAdmin from './pages/PlatformAdmin';
import ComingSoon from './pages/ComingSoon';
import Settings from './pages/Settings';
import Billing from './pages/Billing';
import FAQ from './pages/FAQ';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Pricing from './pages/Pricing';
import Compliance from './pages/Compliance';
import Analytics from './pages/Analytics';
import ContentLibrary from './pages/ContentLibrary';
import LicenseAgreement from './pages/LicenseAgreement';

// ─── SVG Icon Components ────────────────────────────────────────────────────
const Icon = ({ d, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d={d} />
  </svg>
);

const Icons = {
  dashboard: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  location: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>,
  channel: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.4"/><circle cx="12" cy="12" r="2"/><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.4"/><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"/></svg>,
  announce: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 11 18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>,
  jingle: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>,
  program: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  news: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/></svg>,
  calendar: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/></svg>,
  ads: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>,
  admin: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  team: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  settings: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  billing: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  shield: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  faq: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  shoutouts: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>,
  analytics: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/></svg>,
  contentLib: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m16 6 4 14"/><path d="M12 6v14"/><path d="M8 8v12"/><path d="M4 4v16"/></svg>,
  relay: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1"/></svg>,
  license: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>,
  logout: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16,17 21,12 16,7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  groovie: (p) => <svg {...p} xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="15.5" r="2.5"/><path d="M8 17V5l12-2v12"/></svg>,
};

// ─── Protected Route ────────────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function TrialBanner() {
  // Global account-state banner (S2). Honest states per FPA §6: trial countdown, Supported Mode,
  // and Deactivated. Reads status + supported/deactivated fields from /billing/current.
  const [info, setInfo] = React.useState(null);
  React.useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      fetch("https://groovie-business-api-production.up.railway.app/api/v1/billing/current", {
        headers: { Authorization: "Bearer " + token }
      }).then(r => r.json()).then(data => {
        const s = data.status;
        if (s === "trial" && data.trial_ends_at) {
          const days = Math.max(0, Math.ceil((new Date(data.trial_ends_at) - new Date()) / 86400000));
          setInfo({ kind: "trial", days });
        } else if (s === "supported") {
          setInfo({ kind: "supported" });
        } else if (s === "deactivated" || s === "expired") {
          setInfo({ kind: "deactivated" });
        }
      }).catch(() => {});
    } catch(e) {}
  }, []);
  if (!info) return null;
  var THEME = {
    trial:       { bg: "linear-gradient(90deg, #7c3aed, #a855f7)",
                   msg: "Free trial: " + info.days + " day" + (info.days !== 1 ? "s" : "") + " remaining. Subscribe anytime to keep your station running.",
                   cta: "View Plans" },
    supported:   { bg: "linear-gradient(90deg, #b45309, #d97706)",
                   msg: "Supported Mode — your setup is saved; subscribe anytime to restore everything instantly.",
                   cta: "Subscribe" },
    deactivated: { bg: "linear-gradient(90deg, #dc2626, #ef4444)",
                   msg: "Deactivated — your setup is saved for 12 months; subscribe to reactivate and restore everything instantly.",
                   cta: "Reactivate" },
  };
  var t = THEME[info.kind];
  return React.createElement("div", {
    style: {
      padding: "10px 24px", display: "flex", alignItems: "center", justifyContent: "space-between",
      background: t.bg, color: "#fff", fontSize: 13, fontWeight: 600, fontFamily: "Outfit,sans-serif",
    }
  },
    React.createElement("span", null, t.msg),
    React.createElement("a", {
      href: "/billing",
      style: { background: "rgba(255,255,255,0.2)", padding: "5px 14px", borderRadius: 6, color: "#fff", textDecoration: "none", fontSize: 12, fontWeight: 700 }
    }, t.cta)
  );
}

// ─── Nav Items ──────────────────────────────────────────────────────────────
const navSections = [
  {
    label: 'MAIN',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
      { path: '/locations', label: 'Locations', icon: 'location' },
      { path: '/channels', label: 'Channels', icon: 'channel' },
    ]
  },
  {
    label: 'CONTENT',
    items: [
      { path: '/announcements', label: 'Announcements', icon: 'announce' },
      { path: '/jingles', label: 'Jingles', icon: 'jingle' },
      { path: '/radio-programs', label: 'Programs', icon: 'program' },
      { path: '/news', label: 'News', icon: 'news' },
      { path: '/calendar', label: 'Calendar', icon: 'calendar' },
      { path: '/shoutouts', label: 'DJ Shoutouts', icon: 'shoutouts' },
      { path: '/ads', label: 'Ads', icon: 'ads' },
      { path: '/analytics', label: 'Analytics', icon: 'analytics' },
      { path: '/content-library', label: 'Content Library', icon: 'contentLib' },
    ]
  },
  {
    label: 'MANAGE',
    items: [
      { path: '/platform-admin', label: 'Platform Admin', icon: 'admin', adminOnly: true },
      { path: '/license', label: 'Music License', icon: 'license' },
      { path: '/compliance', label: 'Compliance', icon: 'shield' },
      { path: '/team', label: 'Team · Soon', icon: 'team' },
      { path: '/settings', label: 'Settings', icon: 'settings' },
      { path: '/billing', label: 'Billing', icon: 'billing' },
      { path: '/faq', label: 'Help & FAQ', icon: 'faq' },
    ]
  }
];

// ─── Dashboard Layout ───────────────────────────────────────────────────────
function DashboardLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('company');
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const isPlatformAdmin = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.is_platform_admin === true;
    } catch { return false; }
  })();

  return (
    <div className="flex min-h-screen" style={{ background: 'linear-gradient(180deg, #f0f0f3 0%, #e8e8ec 100%)' }}>
      {/* ── Sidebar ── */}
      <aside className="fixed top-0 left-0 bottom-0 w-64 flex flex-col" style={{
        background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        zIndex: 50,
      }}>
        {/* Logo — links back to the platform homepage (H1 cross-layer home nav) */}
        <div className="px-5 py-5 flex items-center gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer' }} onClick={() => { window.location.href = 'https://groovie.live'; }} title="Groovie home">
          <div style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
            borderRadius: '10px',
            padding: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {Icons.groovie({ style: { color: 'white' } })}
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: '17px', letterSpacing: '-0.3px', fontFamily: "'Outfit', 'DM Sans', sans-serif" }}>groovie</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontWeight: 500, letterSpacing: '0.5px' }}>business</div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3" style={{ scrollbarWidth: 'none' }}>
          {navSections.map(section => (
            <div key={section.label} className="mb-5">
              <div style={{
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '1.5px',
                color: 'rgba(255,255,255,0.25)',
                padding: '0 12px',
                marginBottom: '8px',
                fontFamily: "'Outfit', 'DM Sans', sans-serif",
              }}>{section.label}</div>
              {section.items
                .filter(item => !item.adminOnly || isPlatformAdmin)
                .map(item => {
                  const active = isActive(item.path);
                  const IconComponent = Icons[item.icon];
                  return (
                    <button key={item.path} onClick={() => navigate(item.path)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-all duration-150"
                      style={{
                        background: active ? 'rgba(124, 58, 237, 0.15)' : 'transparent',
                        color: active ? '#c4b5fd' : 'rgba(255,255,255,0.55)',
                        fontSize: '13.5px',
                        fontWeight: active ? 600 : 500,
                        fontFamily: "'Outfit', 'DM Sans', sans-serif",
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        borderLeft: active ? '3px solid #7c3aed' : '3px solid transparent',
                      }}
                      onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; } }}
                      onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; } }}
                    >
                      {IconComponent && <IconComponent style={{ flexShrink: 0, opacity: active ? 1 : 0.7 }} />}
                      {item.label}
                    </button>
                  );
                })}
            </div>
          ))}
        </nav>

        {/* Logout */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '12px' }}>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150"
            style={{
              background: 'transparent',
              color: 'rgba(255,255,255,0.4)',
              fontSize: '13.5px',
              fontWeight: 500,
              fontFamily: "'Outfit', 'DM Sans', sans-serif",
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
          >
            {Icons.logout({})}
            Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="ml-64 flex-1 min-h-screen">
        <TrialBanner />
        {children}
      </main>
    </div>
  );
}

// ─── App ────────────────────────────────────────────────────────────────────
function App() {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><Dashboard /></DashboardLayout></ProtectedRoute>} />
        <Route path="/locations" element={<ProtectedRoute><DashboardLayout><Locations /></DashboardLayout></ProtectedRoute>} />
        <Route path="/channels" element={<ProtectedRoute><DashboardLayout><Channels /></DashboardLayout></ProtectedRoute>} />
        <Route path="/announcements" element={<ProtectedRoute><DashboardLayout><Announcements /></DashboardLayout></ProtectedRoute>} />
        <Route path="/jingles" element={<ProtectedRoute><DashboardLayout><Jingles /></DashboardLayout></ProtectedRoute>} />
        <Route path="/radio-programs" element={<ProtectedRoute><DashboardLayout><RadioPrograms /></DashboardLayout></ProtectedRoute>} />
        <Route path="/news" element={<ProtectedRoute><DashboardLayout><News /></DashboardLayout></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><DashboardLayout><Calendar /></DashboardLayout></ProtectedRoute>} />
        <Route path="/shoutouts" element={<ProtectedRoute><DashboardLayout><Shoutouts /></DashboardLayout></ProtectedRoute>} />
        <Route path="/qrcodes" element={<ProtectedRoute><DashboardLayout><QRCodeGenerator /></DashboardLayout></ProtectedRoute>} />
        <Route path="/ads" element={<ProtectedRoute><DashboardLayout><Ads /></DashboardLayout></ProtectedRoute>} />
        <Route path="/platform-admin" element={<ProtectedRoute><DashboardLayout><PlatformAdmin /></DashboardLayout></ProtectedRoute>} />
        <Route path="/broadcast-status" element={<ProtectedRoute><DashboardLayout><BroadcastStatus /></DashboardLayout></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><DashboardLayout><ComingSoon title="Team Members" note="Invite teammates with roles that actually gate access. We're wiring it into the real permission system — coming soon." /></DashboardLayout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><DashboardLayout><Settings /></DashboardLayout></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><DashboardLayout><Billing /></DashboardLayout></ProtectedRoute>} />

        <Route path="/analytics" element={<ProtectedRoute><DashboardLayout><Analytics /></DashboardLayout></ProtectedRoute>} />
        <Route path="/content-library" element={<ProtectedRoute><DashboardLayout><ContentLibrary /></DashboardLayout></ProtectedRoute>} />
        <Route path="/compliance" element={<ProtectedRoute><DashboardLayout><Compliance /></DashboardLayout></ProtectedRoute>} />
        <Route path="/license" element={<ProtectedRoute><DashboardLayout><LicenseAgreement /></DashboardLayout></ProtectedRoute>} />

        {/* Default redirect */}
        <Route path="/" element={<Home />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;